import type { NumberingStyle } from '@worldathletics/types';

import { formatIndex } from './format-index';

export type NamingNode = {
  id: string;
  type: string;
  style?: string | null;
  numberingStyle: NumberingStyle | null;
  blocks?: NamingNode[];
  propagateNumbering?: boolean;
  continueNumbering?: boolean;
  transparent?: boolean;
  numberingExempt?: boolean;
};

/**
 * Build an `id → dotted positional number` map for numbered containers in the
 * document tree. Shared by editor display (`computeNameMap`) and reference
 * labels (`buildReferenceLabelMap`) so numbering stays in sync.
 *
 * Implements continueNumbering, propagateNumbering, transparency (the
 * `subsection` style or an explicit `transparent` flag), numberingExempt (a
 * block skips its own number and consumes no sibling slot, WITHOUT hoisting
 * its children — contrast with transparency, below), and appendix
 * numbering — see `computeNameMap` for full semantics.
 */
export function computePositionalNumberMap(
  tree: NamingNode[],
  rootStyle: NumberingStyle,
): Map<string, string> {
  const map = new Map<string, string>();

  const flatten = (blocks: NamingNode[]): NamingNode[] => {
    const out: NamingNode[] = [];
    for (const block of blocks) {
      const isTransparent =
        block.style === 'subsection' || block.transparent === true;
      if (isTransparent) {
        out.push(...flatten(block.blocks ?? []));
      } else {
        out.push(block);
      }
    }
    return out;
  };

  const walk = (
    blocks: NamingNode[],
    prefix: string,
    inheritedStyle: NumberingStyle,
    startPosition: number,
  ): number => {
    let position = startPosition;
    let appendixPosition = 0;
    let lastChildEnd = 0;
    for (const block of flatten(blocks)) {
      if (
        block.type === 'text' ||
        block.type === 'table' ||
        block.type === 'image' ||
        block.type === 'list'
      ) {
        continue;
      }

      // A block's OWN number is governed by the style it inherits from its
      // parent — NOT by its own `numberingStyle` (which only steers ITS
      // children). When the parent's style is `none`/`free`, this whole
      // sibling level is unnumbered: the block gets no name, but its children
      // still recurse using the block's own `numberingStyle`.
      const childStyle = block.numberingStyle ?? inheritedStyle;
      if (inheritedStyle === 'none' || inheritedStyle === 'free') {
        map.set(block.id, '');
        lastChildEnd = walk(block.blocks ?? [], '', childStyle, 0);
        continue;
      }

      // A block can opt ITSELF out of numbering entirely: no number of its
      // own, and it consumes no position/appendixPosition slot (so siblings
      // are completely unaffected) — while its own children still recurse
      // normally using ITS OWN `numberingStyle`. Unlike transparency
      // (`flatten`, above), the children are NOT hoisted into this sibling
      // level — they stay nested and get their own independent numbering
      // scope, rooted at position 0.
      if (block.numberingExempt === true) {
        map.set(block.id, '');
        lastChildEnd = walk(block.blocks ?? [], '', childStyle, 0);
        continue;
      }

      const isAppendix = block.style === 'appendix';
      let segment: string;
      if (isAppendix) {
        appendixPosition += 1;
        segment = formatIndex(appendixPosition, 'numeric');
      } else {
        position += 1;
        segment = formatIndex(position, inheritedStyle);
      }
      const displayed = prefix ? `${prefix}.${segment}` : segment;

      map.set(block.id, displayed);

      const childPrefix = block.propagateNumbering === false ? '' : displayed;
      const childStart = block.continueNumbering ? lastChildEnd : 0;
      lastChildEnd = walk(
        block.blocks ?? [],
        childPrefix,
        childStyle,
        childStart,
      );
    }
    return position;
  };

  walk(tree, '', rootStyle, 0);
  return map;
}
